'use client'

import { cn } from '@brew/ui/cn'
import { useState } from 'react'
import {
  getClientBrandFaviconUrl,
  getClientBrandLogoUrl,
} from '@/lib/brand/client-brand-logo-url'

export type McpClientLogoProps = {
  /** Brand domain (e.g. "cursor.com"). */
  domain: string
  /** Client name — used for the alt text and the initial fallback. */
  name: string
  className?: string
}

/**
 * A client/agent logo by domain: context.dev Logo Link → Google favicon →
 * the first letter, degrading silently so a missing logo never leaves a
 * broken image. Logo Link monograms unknown domains, so the later stages
 * only fire on network failure or when the client ID env is unset.
 */
function resolveLogoSrc(domain: string, stage: number): string {
  if (stage === 0) {
    return getClientBrandLogoUrl(domain)
  }
  if (stage === 1) {
    return getClientBrandFaviconUrl(domain)
  }
  return ''
}

export function McpClientLogo({ domain, name, className }: McpClientLogoProps) {
  const [stage, setStage] = useState(0)
  const src = resolveLogoSrc(domain, stage)

  if (!src) {
    return (
      <span
        aria-hidden
        className={cn(
          'flex items-center justify-center rounded bg-muted font-semibold text-caption text-muted-foreground',
          className
        )}
      >
        {name.charAt(0)}
      </span>
    )
  }

  return (
    // biome-ignore lint/performance/noImgElement: context.dev Logo Link, not Next-optimizable
    // biome-ignore lint/correctness/useImageSize: pre-Ultracite baseline — add dimensions when touching
    <img
      src={src}
      alt={`${name} logo`}
      className={cn('rounded object-contain', className)}
      decoding="async"
      onError={() => setStage((s) => s + 1)}
    />
  )
}
